import React, { useState, useEffect, useCallback } from 'react';
import {
    FunnelSimple,
    SpinnerGap,
    Buildings,
} from '@phosphor-icons/react';
import {
    getDealSourcingDeals,
    getDealSourcingStatus,
    passDeal,
    contactDeal,
    getCanonicalInbox,
} from '@/lib/api';
import {
    DS_COLORS,
    DS_BORDERS,
    DS_SPACING,
    DS_TYPOGRAPHY,
} from '@/config/ui-invariants';
import { AGENT_CONFIG, INTENT_TO_BUTTON, WORK_TYPES } from '@/config/task-display-config';
import useAnnouncer from '@/hooks/useAnnouncer';
import { DealCard } from '@/components/shared/DealCard';
import { toast } from 'sonner';

/**
 * Deal Sourcing Agent — Inbound deals from Tracxn LiveDeals.
 * Authority: docs/agents/lead-gen/TRACXN_LIVEDEALS_SPEC.md
 *
 * Route: /agents/deal-sourcing — Sidebar: Sourcing section.
 *
 * WI-SHARED-DEAL-CARD-001 PR D: this file is now the page shell only.
 * Tabs, fetch logic, stats, and empty state stay; the per-row card body
 * is delegated to the shared @/components/shared/DealCard. The previous
 * inline ~500-line DealCard function and CollapsibleCardHeader helper
 * have been removed — the same render path now powers the Work Inbox
 * detail view, eliminating drift between the two surfaces.
 */

const DS_CONFIG = AGENT_CONFIG.deal_sourcing || {};
const DS_TABS = DS_CONFIG.tabs || [
    { id: 'pending', label: 'Pending' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'archive', label: 'Archive' },
];
const DS_LABELS = DS_CONFIG.labels || {};

/**
 * The GET /tracxn/deals endpoint returns tracxn_deals docs which do not
 * include the dev_task id needed for the Work Inbox approve endpoint.
 * Look up dev_tasks by category=tracxn_livedeals to build a
 * deal_id → task_id mapping.
 */
async function fetchDealTaskMap() {
    try {
        const data = await getCanonicalInbox(null, 50);
        const items = Array.isArray(data) ? data : data.items || [];
        const map = {};
        items
            .filter((item) => item.work_type === WORK_TYPES.TRACXN_LIVEDEALS || item.work_type === WORK_TYPES.FIRST_LOOK)
            .forEach((item) => {
                const dealId = item.payload?.deal_id;
                if (dealId && item.id) map[dealId] = item.id;
            });
        return map;
    } catch {
        return {};
    }
}

/* --------- TabChips --------- */

function TabChips({ activeTab, onTabChange, tabCounts }) {
    return (
        <div className="flex items-center gap-2 mb-6">
            {DS_TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                const count = tabCounts[tab.id] ?? 0;
                const tone = isActive ? INTENT_TO_BUTTON.primary : INTENT_TO_BUTTON.neutral;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => onTabChange(tab.id)}
                        className={`px-4 py-2 h-11 rounded-full border font-heading text-sm font-semibold inline-flex items-center transition-colors ${tone.bg} ${tone.text} ${tone.border} ${tone.hover}`}
                        aria-pressed={isActive}
                    >
                        {tab.label}
                        {count > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center text-xs">
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

/* --------- Main Component --------- */

export default function DealSourcingAgent() {
    const [activeTab, setActiveTab] = useState('pending');
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [, setDealTaskMap] = useState({});
    // Leg 5 (a11y): page-level polite live region, wired into each row's
    // DealCard via onAnnounce. A per-row DealCard's own local live region
    // unmounts along with the row when Pass/Contacted succeeds (the row
    // leaves `deals`) — this page-level region persists across that removal
    // so a screen reader still hears the outcome. Authority: #950/#954
    // pattern in LinkedInPostAgent.jsx.
    // WI-POST-BUTTON-REGRESSION-HUNT-001 — via useAnnouncer, not a bare setState:
    // a repeated identical outcome is a React no-op and would be spoken once and
    // never again (the Post Studio failure class). See the hook for the full why.
    const [statusMessage, announce] = useAnnouncer();

    const tabCounts = {
        pending:
            (stats?.by_status?.pending_enrichment || 0) +
            (stats?.by_status?.pending_review || 0),
        pipeline: stats?.by_status?.contacted || 0,
        archive: (stats?.by_status?.passed || 0) + (stats?.by_status?.filtered_out || 0),
    };

    useEffect(() => {
        getDealSourcingStatus()
            .then(setStats)
            .catch((err) => console.error('[DealSourcing] Status fetch failed', err));

        fetchDealTaskMap()
            .then(setDealTaskMap)
            .catch(() => setDealTaskMap({}));
    }, []);

    const fetchDeals = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (activeTab === 'pending') {
                const data = await getDealSourcingDeals('pending_review', 50);
                setDeals(Array.isArray(data) ? data : data.deals || []);
            } else if (activeTab === 'pipeline') {
                const data = await getDealSourcingDeals('contacted', 50);
                setDeals(Array.isArray(data) ? data : data.deals || []);
            } else {
                const [passedData, filteredData] = await Promise.all([
                    getDealSourcingDeals('passed', 50),
                    getDealSourcingDeals('filtered_out', 50),
                ]);
                const passed = Array.isArray(passedData) ? passedData : passedData.deals || [];
                const filtered = Array.isArray(filteredData)
                    ? filteredData
                    : filteredData.deals || [];
                setDeals([...passed, ...filtered]);
            }
        } catch (err) {
            console.error('[DealSourcing] Fetch failed', err);
            setError('Could not load deals');
            setDeals([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchDeals();
    }, [fetchDeals]);

    /* Page-level state-updaters: filter the local list and refresh stats so
     * counts stay in sync with the live tracxn_deals collection. The shared
     * DealCard owns the spinner + button-disable; these handlers fire only
     * after the API call resolves successfully. */
    const removeDealLocally = useCallback((dealId) => {
        setDeals((prev) => prev.filter((d) => d._id !== dealId));
        getDealSourcingStatus().then(setStats).catch(() => {});
        fetchDealTaskMap().then(setDealTaskMap).catch(() => {});
    }, []);

    const handlePass = useCallback(
        async (dealId, reason) => {
            try {
                await passDeal(dealId, reason || null);
                toast.success(DS_LABELS.passSuccess || 'Deal passed.');
                removeDealLocally(dealId);
            } catch (err) {
                console.error('[DealSourcing] passDeal failed', err);
                toast.error(DS_LABELS.passError || 'Could not pass — please try again.');
                // DECISION: rethrow so DealCard's own try/catch (Leg 5) can tell
                // its aria-live announcer this failed, not just the toast above
                // (toasts are not reliably announced to a screen reader).
                throw err;
            }
        },
        [removeDealLocally]
    );

    const handleContacted = useCallback(
        async (dealId) => {
            try {
                await contactDeal(dealId);
                toast.success(DS_LABELS.contactedSuccess || 'Marked as contacted.');
                removeDealLocally(dealId);
            } catch (err) {
                console.error('[DealSourcing] contactDeal failed', err);
                toast.error(DS_LABELS.contactedError || 'Could not update — please try again.');
                throw err; // See DECISION in handlePass above.
            }
        },
        [removeDealLocally]
    );

    const emptyLabels = {
        pending: DS_LABELS.pendingEmpty || 'No deals waiting for review.',
        pipeline: DS_LABELS.pipelineEmpty || 'No contacted deals yet.',
        archive: DS_LABELS.archiveEmpty || 'Passed and filtered deals will appear here.',
    };

    return (
        <div
            style={{
                maxWidth: (DS_SPACING.pageMaxWidth || 1200) + 'px',
                margin: '0 auto',
                padding: '32px 24px',
            }}
        >
            {/* Leg 5 (a11y): page-level polite live region — see the
                statusMessage/announce declaration above for why this must
                live here rather than inside each row's DealCard. */}
            <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

            <div style={{ marginBottom: DS_SPACING.cardGap + 'px' }}>
                <div className="flex items-center gap-3">
                    <FunnelSimple
                        weight="bold"
                        className="w-7 h-7"
                        style={{ color: DS_COLORS.text.brand }}
                    />
                    <h1
                        style={{
                            ...(DS_TYPOGRAPHY.displayXL || DS_TYPOGRAPHY.listItemTitle),
                            color: DS_COLORS.text.primary,
                            letterSpacing: '-0.025em',
                        }}
                    >
                        {DS_CONFIG.label || 'Deal Sourcing'}
                    </h1>
                </div>
                <p
                    style={{
                        ...DS_TYPOGRAPHY.bodyMd,
                        color: DS_COLORS.text.secondary,
                        marginTop: '8px',
                    }}
                >
                    {DS_CONFIG.description || ''}
                </p>
            </div>

            <TabChips
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabCounts={tabCounts}
            />

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <SpinnerGap
                        weight="bold"
                        className="w-8 h-8 animate-spin"
                        style={{ color: DS_COLORS.text.brand }}
                    />
                </div>
            ) : error ? (
                <div
                    className="text-center py-12 rounded-lg"
                    style={{
                        background: DS_COLORS.surface.warm,
                        border: (DS_BORDERS.width || '1px') + ' solid ' + DS_BORDERS.color,
                    }}
                >
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.secondary }}>
                        {error}
                    </p>
                </div>
            ) : deals.length === 0 ? (
                <div
                    className="text-center py-16 rounded-lg"
                    style={{
                        background: DS_COLORS.surface.warm,
                        border: (DS_BORDERS.width || '1px') + ' solid ' + DS_BORDERS.color,
                    }}
                >
                    <Buildings
                        weight="light"
                        className="w-12 h-12 mx-auto"
                        style={{ color: DS_COLORS.text.muted }}
                    />
                    <p
                        style={{
                            ...DS_TYPOGRAPHY.bodyMd,
                            color: DS_COLORS.text.secondary,
                            marginTop: '12px',
                        }}
                    >
                        {emptyLabels[activeTab]}
                    </p>
                </div>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: DS_SPACING.cardGap + 'px',
                    }}
                >
                    {deals.map((deal) => (
                        <DealCard
                            key={deal._id}
                            deal={deal}
                            surface="agents"
                            onPass={handlePass}
                            onContacted={handleContacted}
                            onRefresh={fetchDeals}
                            onAnnounce={announce}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
